// src/components/modals/OrderDetailModal.jsx
import React, { useState } from 'react';

const OrderDetailModal = ({ show, onHide, order, currency, onStatusUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  
  const formatCurrency = (amount) => {
    if (!amount) return currency === 'NGN' ? '₦0.00' : '$0.00';
    try {
      return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return currency === 'NGN' ? `₦${amount.toFixed(2)}` : `$${amount.toFixed(2)}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'delivered':
      case 'completed':
        return "bg-green-100 text-green-800 text-xs";
      case 'processing':
      case 'pending':
      case 'payment_pending':
        return "bg-yellow-100 text-yellow-800 text-xs";
      case 'shipped':
      case 'scheduled':
        return "bg-blue-100 text-blue-800 text-xs";
      case 'cancelled':
      case 'failed':
        return "bg-red-100 text-red-800 text-xs";
      case 'returned':
        return "bg-purple-100 text-purple-800 text-xs";
      case 'accepted':
        return "bg-green-50 text-green-700 text-xs";
      default:
        return "bg-gray-100  text-xs";
    }
  };

  const getOrderTypeBadge = (orderType) => {
    if (orderType?.toLowerCase() === 'service') {
      return "bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full";
    }
    return "bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full";
  };

  const getOrderIcon = (orderType) => {
    return orderType?.toLowerCase() === 'service' ? '🔧' : '📦';
  };

  const isServiceOrder = order?.order_type === 'service';

  const handleStatusChange = async (newStatus) => {
    if (!order || !onStatusUpdate) return;
    
    // Show confirmation for shipping action
    if (newStatus === 'shipped') {
      const confirmed = window.confirm(
        '⚠️ Important: Once you mark this order as shipped, this action cannot be undone. Are you sure you want to proceed?'
      );
      if (!confirmed) return;
    }
    
    // Show confirmation for manual payment marking
    if ((newStatus === 'completed' || newStatus === 'paid') && order.payment_status === 'pending') {
      const confirmed = window.confirm(
        '💳 Manual Payment Confirmation: Are you sure you want to mark this order as paid? This should only be used if payment has been confirmed through external means or to resolve system errors.'
      );
      if (!confirmed) return;
    }
    
    setIsUpdating(true);
    const success = await onStatusUpdate(order.order_id, newStatus);
    setIsUpdating(false);
    
    if (success) {
      // Optionally close the modal or show a success message
    }
  };

  const calculateOrderTotal = () => {
    if (order.items && order.items.length > 0) {
      return order.items.reduce((total, item) => {
        const price = parseFloat(item.per_unit_cost|| item.price || 0);
        const quantity = parseInt(item.order_quantity || item.quantity || 1);
        return total + (price * quantity);
      }, 0);
    }
    return parseFloat(order.total_amount || 0);
  };

  const getAvailableStatuses = () => {
    if (isServiceOrder) {
      return ['pending', 'accepted', 'scheduled', 'completed', 'cancelled'];
    } else {
      return ['pending', 'shipped', 'delivered', 'cancelled', 'returned'];
    }
  };

  const getStatusDisplayName = (status) => {
    const statusMap = {
      pending: 'Pending',
      accepted: 'Accepted',
      scheduled: 'Scheduled',
      shipped: 'Shipped',
      delivered: 'Delivered',
      completed: 'Completed',
      cancelled: 'Cancelled',
      returned: 'Returned'
    };
    return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
  };

  const isStatusDisabled = (status) => {
    const currentStatus = order.order_status?.toLowerCase();
    
    if (isServiceOrder) {
      // Service order status flow
      switch (status) {
        case 'pending':
          return currentStatus !== 'pending';
        case 'accepted':
          return currentStatus !== 'pending';
        case 'scheduled':
          return !['accepted', 'pending'].includes(currentStatus);
        case 'completed':
          return !['scheduled', 'accepted', 'pending'].includes(currentStatus);
        case 'cancelled':
          return currentStatus === 'completed' || currentStatus === 'cancelled';
        default:
          return false;
      }
    } else {
      // Product order status flow
      switch (status) {
        case 'pending':
          return currentStatus !== 'pending';
        case 'shipped':
          return currentStatus !== 'pending';
        case 'delivered':
          return currentStatus !== 'shipped';
        case 'returned':
          return currentStatus !== 'delivered';
        case 'cancelled':
          return currentStatus === 'delivered' || currentStatus === 'shipped' || currentStatus === 'cancelled';
        default:
          return false;
      }
    }
  };

  const getStatusTooltip = (status) => {
    if (isServiceOrder) {
      switch (status) {
        case 'accepted':
          return 'Accept the service booking to proceed';
        case 'scheduled':
          return 'Schedule after accepting the booking';
        case 'completed':
          return 'Mark as completed to finalize service';
        case 'cancelled':
          return 'Can only cancel before completion';
        default:
          return '';
      }
    } else {
      switch (status) {
        case 'shipped':
          return 'Mark as shipped when items are dispatched';
        case 'delivered':
          return 'Can only mark delivered after shipping';
        case 'returned':
          return 'Can only process returns after delivery';
        case 'cancelled':
          return 'Cannot cancel after shipping';
        default:
          return '';
      }
    }
  };

  // Check if payment can be manually marked
  const canMarkAsPaid = () => {
    if (order.payment_status === 'completed' || order.payment_status === 'paid') {
      return false; // Already paid
    }
    
    // For service orders, can mark as paid if in scheduled, accepted, or pending status
    if (isServiceOrder) {
      return ['scheduled', 'accepted', 'pending', 'completed'].includes(order.order_status?.toLowerCase());
    }
    
    // For product orders, can mark as paid if in delivered, shipped, or pending status
    return ['delivered', 'shipped', 'pending'].includes(order.order_status?.toLowerCase());
  };

  const getMarkAsPaidTooltip = () => {
    if (order.payment_status === 'completed' || order.payment_status === 'paid') {
      return 'Payment already completed';
    }
    
    if (isServiceOrder) {
      return 'Mark service as paid (can be used to resolve payment system errors)';
    }
    
    return 'Mark order as paid (can be used to resolve payment system errors)';
  };

  if (!show || !order) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl w-[95%] sm:w-[90%] md:w-[85%] lg:max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start sm:items-center p-4 sm:p-6 border-b border-gray-200">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{getOrderIcon(order.order_type)}</span>
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {isServiceOrder ? 'Service Booking' : 'Order'} #{order.order_id}
              </h2>
              <span className={getOrderTypeBadge(order.order_type)}>
                {isServiceOrder ? 'Service' : 'Product'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`px-2 py-1 rounded-full ${getStatusBadgeClass(order.order_status)}`}>
                {order.order_status || 'Unknown'}
              </span>
              <span className={`px-2 py-1 rounded-full ${getStatusBadgeClass(order.payment_status)}`}>
                Payment: {order.payment_status || 'Unknown'}
              </span>
            </div>
          </div>
          <button 
            onClick={onHide} 
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 text-xl sm:text-2xl ml-3 p-1"
          >
            &times;
          </button>
        </div>
        
        {/* Tabs - Mobile Responsive */}
        <div className="border-b border-gray-200">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="inline-flex flex-nowrap min-w-max px-3 sm:px-6">
              <button
                onClick={() => setActiveTab('details')}
                className={`flex-shrink-0 px-3 sm:px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === 'details'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('items')}
                className={`flex-shrink-0 px-3 sm:px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === 'items'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                {isServiceOrder ? 'Service Details' : 'Items'} ({order.items?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('customer')}
                className={`flex-shrink-0 px-3 sm:px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === 'customer'
                    ? 'text-blue-600 border-blue-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                Customer
              </button>
              {isServiceOrder && order.service_details && (
                <button
                  onClick={() => setActiveTab('project')}
                  className={`flex-shrink-0 px-3 sm:px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap border-b-2 ${
                    activeTab === 'project'
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700'
                  }`}
                >
                  Project Info
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 sm:p-6">
          {activeTab === 'details' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {isServiceOrder ? 'Service Information' : 'Order Information'}
                  </h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">{isServiceOrder ? 'Booking ID' : 'Order ID'}:</span>
                      <span className="text-gray-900 font-medium">{order.order_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">{isServiceOrder ? 'Booking Date' : 'Order Date'}:</span>
                      <span className="text-gray-900">{formatDate(order.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Invoice Code:</span>
                      <span className="text-gray-900">{order.invoice_code || 'N/A'}</span>
                    </div>
                    {!isServiceOrder && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Expected Delivery:</span>
                        <span className="text-gray-900 text-right">
                          {order.expected_delivery_earliest && order.expected_delivery_latest 
                            ? `${formatDate(order.expected_delivery_earliest)} - ${formatDate(order.expected_delivery_latest)}`
                            : 'Not specified'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment Information</h3>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Amount:</span>
                      <span className="text-gray-900 font-semibold">{formatCurrency(calculateOrderTotal())}</span>
                    </div>
                    {isServiceOrder && order.upfront_payment_percentage > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Upfront Payment:</span>
                        <span className="text-gray-900">
                          {order.upfront_payment_percentage}% ({formatCurrency((calculateOrderTotal() * order.upfront_payment_percentage) / 100)})
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Payment Status:</span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        order.payment_status === 'completed' || order.payment_status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.payment_status || 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {order.additional_info && (
                <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h3>
                  <p className="text-xs text-gray-600">
                    {order.additional_info}
                  </p>
                </div>
              )}
              
              {/* Status Update Actions */}
              <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Update {isServiceOrder ? 'Service' : 'Order'} Status</h3>
                
                {/* Status Flow Information */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium">
                    {isServiceOrder ? (
                      '📋 Service Flow: Pending → Accepted → Scheduled → Completed. Cancellations only allowed before completion.'
                    ) : (
                      '📦 Order Flow: Pending → Shipped → Delivered → Returned (if needed). Shipping cannot be undone.'
                    )}
                  </p>
                  {(order.payment_status === 'pending' || order.payment_status === 'payment_pending') && (
                    <p className="text-xs text-blue-700 mt-1">
                      💡 <strong>Payment Note:</strong> You can manually mark as paid to resolve system errors if payment was confirmed externally.
                    </p>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-2">
                  {getAvailableStatuses().map(status => {
                    const isDisabled = isStatusDisabled(status) || isUpdating || order.order_status === status;
                    const tooltip = getStatusTooltip(status);
                    
                    return (
                      <div key={status} className="relative group">
                        <button
                          onClick={() => handleStatusChange(status)}
                          disabled={isDisabled}
                          className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                            order.order_status === status
                              ? 'bg-blue-600 text-white'
                              : isDisabled
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={tooltip}
                        >
                          {getStatusDisplayName(status)}
                          {isUpdating && order.order_status === status && '...'}
                        </button>
                        
                        {/* Tooltip */}
                        {tooltip && isDisabled && (
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                            {tooltip}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Important Notice for Shipping */}
                {!isServiceOrder && order.order_status === 'pending' && (
                  <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-700">
                      ⚠️ <strong>Important:</strong> Once you mark an order as "Shipped", this action cannot be undone. 
                      Please ensure all items are properly packaged and dispatched before proceeding.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'items' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">
                {isServiceOrder ? 'Service Details' : 'Order Items'}
              </h3>
              {order.items && order.items.length > 0 ? (
                <div className="space-y-3">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 border-b border-gray-200 pb-3">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {item.reference_image_url ? (
                          <img 
                            src={item.reference_image_url} 
                            alt={item.item_description || item.product_name} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className={`w-full h-full ${isServiceOrder ? 'bg-purple-100' : 'bg-blue-100'} flex items-center justify-center`}>
                            <span className="text-sm font-semibold text-purple-800">
                              {isServiceOrder ? '🔧' : ((item.item_description || item.product_name || '?').charAt(0))}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium  truncate">
                          {item.item_description || item.product_name || 'Unknown Item'}
                        </h4>
                        {isServiceOrder && item.project_title && (
                          <p className="text-xs text-gray-700 mt-1">
                            <strong>Project:</strong> {item.project_title}
                          </p>
                        )}
                        {!isServiceOrder && item.variation && (
                          <p className="text-xs text-gray-600 mt-1">Variant: {item.variation}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {item.order_quantity || item.quantity || 1} × {formatCurrency(item.per_unit_cost|| item.price || 0)}
                        </p>
                        {isServiceOrder && item.upfront_payment_percentage > 0 && (
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                            <p className="text-yellow-800">
                              <strong>Upfront Payment:</strong> {item.upfront_payment_percentage}% 
                              ({formatCurrency((item.per_unit_cost * item.upfront_payment_percentage) / 100)})
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900">
                          {formatCurrency((item.per_unit_cost|| item.price || 0) * (item.order_quantity || item.quantity || 1))}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <span className="text-sm font-semibold text-gray-700">Total:</span>
                    <span className="text-base font-bold text-gray-900">{formatCurrency(calculateOrderTotal())}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-500 text-center py-4">
                  {isServiceOrder ? 'No service details found.' : 'No items found for this order.'}
                </p>
              )}
            </div>
          )}
          
          {activeTab === 'customer' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Customer Information</h3>
              <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-3">Contact Details</h4>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Name:</span>
                        <span className="text-gray-900">{order.customer_name || 'Not provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Email:</span>
                        <span className="text-gray-900 truncate">{order.customer_email || 'Not provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Phone:</span>
                        <span className="text-gray-900">{order.customer_phone || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-3">
                      {isServiceOrder ? 'Service Information' : 'Delivery Address'}
                    </h4>
                    {!isServiceOrder ? (
                      <p className="text-xs text-gray-600 break-words">
                        {order.customer_address || 'No address provided'}
                      </p>
                    ) : (
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Service Type:</span>
                          <span className="text-gray-900">Custom Service</span>
                        </div>
                        {order.service_details?.budget && (
                          <div className="flex justify-between">
                            <span className="text-gray-500">Agreed Budget:</span>
                            <span className="text-gray-900">{formatCurrency(order.service_details.budget)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'project' && isServiceOrder && order.service_details && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Project Information</h3>
              <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 space-y-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Project Title</h4>
                  <p className="text-sm ">{order.service_details.project_title || 'No title provided'}</p>
                </div>
                
                <div>
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">Project Description</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {order.service_details.project_description || 'No description provided'}
                  </p>
                </div>
                
                {order.service_details.reference_image_url && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">Reference Image</h4>
                    <div className="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={order.service_details.reference_image_url} 
                        alt="Reference"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200">
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">Service</h4>
                    <p className="text-sm ">{order.service_details.service_name || 'N/A'}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 mb-1">Budget</h4>
                    <p className="text-sm ">{formatCurrency(order.service_details.budget || 0)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onHide}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-white transition-colors w-full sm:w-auto order-2 sm:order-1"
          >
            Close
          </button>
          
          {/* Manual Payment Marking Button */}
          {canMarkAsPaid() && (
            <div className="relative group">
              <button
                onClick={() => handleStatusChange(isServiceOrder ? 'completed' : 'paid')}
                disabled={isUpdating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto order-1 sm:order-2"
                title={getMarkAsPaidTooltip()}
              >
                {isUpdating ? 'Processing...' : `Mark as ${isServiceOrder ? 'Completed' : 'Paid'}`}
              </button>
              
              {/* Tooltip for manual payment */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-10">
                {getMarkAsPaidTooltip()}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;